import { useCallback, useEffect, useRef, useState } from 'react';

import { lumioConversationFirstMessage, lumioListConversations } from './endpoint';

export interface ConversationListItem {
  id: string;
  /** First user message (the list title); null when it couldn't be derived. */
  title: string | null;
  /** ISO timestamp of the last message, for the relative-time label. */
  lastMessageAt: string | null;
}

export interface UseLumioConversations {
  items: ConversationListItem[];
  loading: boolean;
  error: boolean;
  /** Re-fetch the list (after an error). */
  reload: () => void;
}

/** How many recent conversations the dropdown lists. */
const LIST_LIMIT = 20;

/**
 * Fetches the owner's recent conversations (newest-first) for the history
 * dropdown, deriving each item's title from its first message (the conversations
 * endpoint carries no title). Fetches when `open` becomes true and aborts on close;
 * no fallback: shows loading, then the real list or an honest error. Conversations
 * with no first message (an unused init) are dropped; a single preview that fails
 * leaves that item title-less rather than failing the whole list.
 */
export const useLumioConversations = (open: boolean): UseLumioConversations => {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback((): void => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError(false);
    setLoading(true);
    lumioListConversations(LIST_LIMIT, controller.signal)
      .then((convs) => {
        // Skip empty conversations (an unused /chat/init has no messages) and any
        // repeated id, so the list never shows blank or duplicate rows.
        const seen = new Set<string>();
        const visible = convs.filter((c) => {
          if (c.checkpoint_count <= 0 || seen.has(c.conversation_id)) return false;
          seen.add(c.conversation_id);
          return true;
        });
        return Promise.all(
          visible.map((c) =>
            lumioConversationFirstMessage(c.conversation_id, controller.signal)
              .catch(() => null)
              .then((title) => ({ id: c.conversation_id, title, lastMessageAt: c.last_message })),
          ),
        );
      })
      .then((list) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setItems(list);
      })
      .catch(() => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setError(true);
      })
      .finally(() => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setLoading(false);
      });
  }, []);

  // Fetch a fresh list each time the dropdown opens (it may include a conversation
  // started since the last open); abort the fetch if it closes first.
  useEffect(() => {
    if (!open) return undefined;
    load();
    return () => abortRef.current?.abort();
  }, [open, load]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  return { items, loading, error, reload: load };
};
